import { formatDate, formatYYMMDD, } from "@erp/shared/utils/date/formatted-date";
import { defaultMemberInfo } from "@erp/shared/components/ui/Dialog/MemberSearchDialog/member-info.const";
import { useMember } from "@erp/shared/components/ui/Dialog/MemberSearchDialog/useMember";
import useEditMode from "@erp/shared/components/ui/Document/_composable/useEditMode";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { OBTAIN_BASIC_INFO, } from "../ObtainWriteTab.types";
import { obtainDefaultInfo, obtainDefaultInfo2, obtainFilesInputs, } from "../ObtainWriteTab.const";
import { findByKey } from "@erp/shared/utils/getProperty";
import { convertToFileObj } from "@erp/shared/utils/file/convert-to-file-obj";
import { useLoginInfo } from "@erp/shared/composables/useLoginInfo";
import { useERP } from "@erp/shared/composables/useERP";
import { checkObtainDuplicate } from "@erp/shared/api/generated/wrappers/obtain";
import { getObtainConfirm } from "@erp/shared/api/generated/wrappers/obtain";
import { getProject } from "@erp/shared/api/generated/wrappers/project";
import { checkProjectDuplicate } from "@erp/shared/api/generated/wrappers/project";
import { useCompanyAutofill } from "@erp/shared/composables/useCompanyAutofill";
export const useObtainBasicInfoStore = defineStore("obtainBasicInfo", () => {
    const { userData } = useLoginInfo();
    const confirmId = ref(undefined);
    const estimateConfirmationId = ref(undefined);
    const member = useMember(defaultMemberInfo, { setChecker: true }, userData);
    const edit = useEditMode();
    const { isEdit } = edit;
    const { codeInfo } = useERP();
    const basicInfoValidate = ref(false);
    const writeIssueDate = ref(formatDate());
    const basicInfoFirstInputs = ref(obtainDefaultInfo(codeInfo.projectPrefix, codeInfo.inhouseBidNumberPrefix));
    const basicInfoSecondInputs = ref(obtainDefaultInfo2);
    const filesInputs = ref(obtainFilesInputs);
    // 업체+담당자 자동 채우기
    const companyAutofill = useCompanyAutofill();
    const inputIssueDate = computed(() => findByKey(basicInfoFirstInputs.value, "label", OBTAIN_BASIC_INFO.발행일));
    const inputProjectCode = computed(() => findByKey(basicInfoFirstInputs.value, "label", OBTAIN_BASIC_INFO.프로젝트코드));
    const inputInhouseBidNumber = computed(() => findByKey(basicInfoFirstInputs.value, "label", OBTAIN_BASIC_INFO.사내견적번호));
    const inputCompanyBidNumber = computed(() => findByKey(basicInfoFirstInputs.value, "label", OBTAIN_BASIC_INFO.기업별입찰번호));
    // 기본 정보(두 번째 그룹)
    const inputDueDate = computed(() => findByKey(basicInfoSecondInputs.value, "label", OBTAIN_BASIC_INFO.납기일));
    const inputDeliveryCondition = computed(() => findByKey(basicInfoSecondInputs.value, "label", OBTAIN_BASIC_INFO.인도조건));
    const inputProjectName = computed(() => findByKey(basicInfoSecondInputs.value, "label", OBTAIN_BASIC_INFO.프로젝트명));
    const inputProjectPeriod = computed(() => findByKey(basicInfoSecondInputs.value, "label", OBTAIN_BASIC_INFO.프로젝트기간));
    const inputRemark = computed(() => findByKey(basicInfoSecondInputs.value, "label", OBTAIN_BASIC_INFO.특이사항));
    const inputApprovalFile = computed(() => findByKey(filesInputs.value, "label", OBTAIN_BASIC_INFO.승인서) ?? null);
    const inputBlueprintFile = computed(() => findByKey(filesInputs.value, "label", OBTAIN_BASIC_INFO.도면) ?? null);
    const inputObtainFile = computed(() => findByKey(filesInputs.value, "label", OBTAIN_BASIC_INFO.수주서) ?? null);
    const inputOtherFiles = computed(() => findByKey(filesInputs.value, "label", OBTAIN_BASIC_INFO.기타) ?? null);
    const setWriteIssueDate = (newDate) => {
        writeIssueDate.value = newDate;
    };
    const setBasicInfoFirstInputs = (inputs) => {
        basicInfoFirstInputs.value = inputs;
    };
    const setBasicInfoSecondInputs = (inputs) => {
        basicInfoSecondInputs.value = inputs;
    };
    const setFilesInputs = (inputs) => {
        filesInputs.value = inputs;
    };
    const setBasicInfoValidate = (value) => {
        basicInfoValidate.value = value;
    };
    /** 새 문서 작성 시 업체/담당자 및 store 상태 초기화 */
    const resetBasicInfo = () => {
        confirmId.value = undefined;
        estimateConfirmationId.value = undefined;
        basicInfoFirstInputs.value = obtainDefaultInfo(codeInfo.projectPrefix, codeInfo.inhouseBidNumberPrefix);
        basicInfoSecondInputs.value = [...obtainDefaultInfo2];
        filesInputs.value = [...obtainFilesInputs];
        companyAutofill.reset();
        initChipSelectOptions();
    };
    /**
     * @description 견적서 기본 정보 적용
     * @param data 견적서 기본 정보 데이터
     * @param isEdit 수정 모드 여부
     */
    const applyBasicInfo = async (data) => {
        if (!data) {
            return;
        }
        // 이전 문서 데이터 초기화
        companyAutofill.reset();
        if (isEdit.value) {
            confirmId.value = data.info.id;
        }
        else {
            // 견적서에서 수주서 생성 시, 견적서 ID 저장
            estimateConfirmationId.value = data.info.id;
        }
        if (inputIssueDate.value) {
            const issueDate = data.info.createdTime.split("T")[0];
            inputIssueDate.value.value = issueDate;
            writeIssueDate.value = issueDate;
        }
        if (inputProjectCode.value) {
            const hasProjectCode = "projectCode" in data.info && data.info.projectCode !== null;
            inputProjectCode.value.value = hasProjectCode
                ? data.info.projectCode
                : codeInfo.projectPrefix + formatYYMMDD();
            if ("chipMeta" in inputProjectCode.value) {
                // 수정모드: manual (기존 값 직접 표시)
                // 견적→수주 변환 시 기존 프로젝트코드 있으면: search (외부 참조)
                // 프로젝트코드 없으면: auto (자동 생성)
                const mode = isEdit.value
                    ? "manual"
                    : hasProjectCode
                        ? "search"
                        : "auto";
                inputProjectCode.value.chipMeta = {
                    mode,
                    selectedId: isEdit.value ? data.info.id : undefined,
                };
            }
        }
        if (inputInhouseBidNumber.value) {
            inputInhouseBidNumber.value.value = data.info.inhouseBidNumber ?? "";
            if ("chipMeta" in inputInhouseBidNumber.value) {
                inputInhouseBidNumber.value.chipMeta = {
                    mode: isEdit.value ? "manual" : "search",
                    selectedId: isEdit.value ? data.info.id : undefined,
                };
            }
        }
        if (inputCompanyBidNumber.value) {
            inputCompanyBidNumber.value.value = data.info.companyBidNumber ?? "";
        }
        if (inputDueDate.value) {
            inputDueDate.value.value = formatDate(data.info.dueDate) ?? "";
        }
        if (inputDeliveryCondition.value && "deliveryCondition" in data.info) {
            inputDeliveryCondition.value.value = data.info.deliveryCondition ?? "";
        }
        if (inputProjectName.value) {
            inputProjectName.value.value = data.info.projectName ?? "";
        }
        if (inputProjectPeriod.value) {
            inputProjectPeriod.value.value = data.info.projectPeriod ?? "";
        }
        if (inputRemark.value) {
            inputRemark.value.value = data.info.remark ?? "";
        }
        // 업체+담당자 자동 채우기 초기화
        await companyAutofill.initFromExisting({
            companyId: data.info.companyId,
            companyName: data.info.companyName ?? "",
            managerId: data.info.managerId,
            managerName: data.info.managerName ?? "",
            managerEmail: data.info.managerEmail ?? "",
            managerPhone: data.info.managerPhone ?? "",
        });
        if (inputApprovalFile.value && data.info.approvalFile) {
            const file = await convertToFileObj(data.info.approvalFile);
            inputApprovalFile.value.value = file;
        }
        if (inputBlueprintFile.value && data.info.blueprintFile) {
            const file = await convertToFileObj(data.info.blueprintFile);
            inputBlueprintFile.value.value = file;
        }
        // obtain
        if (inputObtainFile.value &&
            "obtainFile" in data.info &&
            data.info.obtainFile) {
            const file = await convertToFileObj(data.info.obtainFile);
            inputObtainFile.value.value = file;
        }
        if (inputOtherFiles.value && data.info.etcFiles) {
            if (typeof data.info.etcFiles === "string") {
                data.info.etcFiles = JSON.parse(data.info.etcFiles);
            }
            if (data.info.etcFiles && data.info.etcFiles.length > 0) {
                const names = data.info.etcFiles;
                const files = (await Promise.all(names.map((name) => convertToFileObj(name)))).filter((f) => !!f);
                inputOtherFiles.value.value = files;
            }
            else {
                inputOtherFiles.value.value = null;
            }
        }
    };
    // === chipSelect 검색/중복검사 함수 바인딩 ===
    const initChipSelectOptions = () => {
        // 프로젝트코드
        const projectInput = inputProjectCode.value;
        if (projectInput && "chipOptions" in projectInput) {
            projectInput.chipOptions.searchFn = async (keyword) => {
                const results = await getProject(keyword);
                return results.map((r) => ({
                    id: r.id,
                    displayValue: r.projectCode,
                    name: r.name ?? "",
                    companyName: r.companyName ?? "",
                    status: r.status,
                }));
            };
            projectInput.chipOptions.duplicateCheckFn = checkProjectDuplicate;
        }
        // 사내견적번호
        const bidInput = inputInhouseBidNumber.value;
        if (bidInput && "chipOptions" in bidInput) {
            bidInput.chipOptions.searchFn = async (keyword) => {
                const searchInputs = [
                    {
                        label: "사내 견적번호",
                        key: "inhouseBidNumber",
                        col: "12",
                        sm: "3",
                        lg: "3",
                        value: keyword,
                    },
                ];
                const results = await getObtainConfirm(searchInputs);
                return results.map((r) => ({
                    id: r.id,
                    displayValue: r.inhouseBidNumber,
                    companyName: r.companyName,
                    approvalPhase: r.approvalPhase,
                }));
            };
            bidInput.chipOptions.duplicateCheckFn = checkObtainDuplicate;
        }
    };
    initChipSelectOptions();
    return {
        ...member,
        ...edit,
        basicInfoValidate,
        /** 작성 날짜 */
        writeIssueDate,
        /** 견적서 정보 상단 입력  */
        basicInfoFirstInputs,
        /** 견적서 정보 하단 입력  */
        basicInfoSecondInputs,
        /** 업체+담당자 자동 채우기 */
        companyAutofill,
        /** 첨부 입력  */
        filesInputs,
        inputIssueDate,
        inputProjectCode,
        inputInhouseBidNumber,
        inputCompanyBidNumber,
        inputDueDate,
        inputDeliveryCondition,
        inputProjectName,
        inputProjectPeriod,
        inputRemark,
        inputApprovalFile,
        inputBlueprintFile,
        inputObtainFile,
        inputOtherFiles,
        confirmId,
        estimateConfirmationId,
        // setters
        setWriteIssueDate,
        setBasicInfoValidate,
        setBasicInfoFirstInputs,
        setBasicInfoSecondInputs,
        setFilesInputs,
        applyBasicInfo,
        resetBasicInfo,
    };
});
